import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';

export interface JournalEntryMeta {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface JournalEntryFull {
  id: string;
  encryptedContent: string;
  iv: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateJournalEntryRequest {
  encryptedContent: string;
  iv: string;
  title?: string;
}

export interface UpdateJournalEntryRequest {
  encryptedContent: string;
  iv: string;
  title?: string;
}

export function useJournalEntries() {
  return useQuery({
    queryKey: ['journal-entries'],
    queryFn: async (): Promise<JournalEntryMeta[]> => {
      const response = await apiClient.get('/consumers/journal');
      return response.data.entries;
    },
  });
}

export function useJournalEntry(entryId: string | null) {
  return useQuery({
    queryKey: ['journal-entry', entryId],
    queryFn: async (): Promise<JournalEntryFull> => {
      if (!entryId) throw new Error('Entry ID is required');
      const response = await apiClient.get(`/consumers/journal/${entryId}`);
      return response.data;
    },
    enabled: !!entryId,
  });
}

export function useCreateJournalEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateJournalEntryRequest) => {
      const response = await apiClient.post('/consumers/journal', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
    },
  });
}

export function useUpdateJournalEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: UpdateJournalEntryRequest & { id: string }) => {
      const response = await apiClient.put(`/consumers/journal/${id}`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
      queryClient.invalidateQueries({ queryKey: ['journal-entry', variables.id] });
    },
  });
}

export function useDeleteJournalEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete(`/consumers/journal/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
    },
  });
}
